import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, MapPin, Trash2, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { BUOY_STATUS, dammInfo } from "../lib/constants";
import { inputCls, labelCls } from "./Modals";

export default function BuoyPanel({ projectId, buoys, nodes, refresh }) {
    const [etikett, setEtikett] = useState("");
    const [nodeId, setNodeId] = useState("");
    const [selected, setSelected] = useState(null);
    const [pasteText, setPasteText] = useState("");
    const [review, setReview] = useState(null);
    const [reviewText, setReviewText] = useState("");
    const [busy, setBusy] = useState(null);
    const fileRef = useRef(null);

    const buoy = buoys.find((b) => b.id === selected);

    const [materials, setMaterials] = useState([]);
    const loadMaterials = async () => {
        if (!selected) {
            setMaterials([]);
            return;
        }
        try {
            const r = await api.get(`/buoys/${selected}/materials`);
            setMaterials(r.data);
        } catch { /* ignore */ }
    };
    useEffect(() => { loadMaterials(); }, [selected, buoys]);
    const pending = materials.filter((m) => m.review_status === "vantar_granskning");

    const create = async () => {
        if (!etikett.trim()) return;
        try {
            const r = await api.post(`/projects/${projectId}/buoys`, { etikett, node_id: nodeId || null });
            toast.success("Boj placerad");
            setEtikett("");
            setSelected(r.data.id);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (bid) => {
        await api.delete(`/buoys/${bid}`);
        if (selected === bid) setSelected(null);
        refresh();
    };

    const upload = async (text, filnamn) => {
        if (!text.trim() || !buoy) return;
        setBusy("deidentify");
        try {
            const r = await api.post(`/buoys/${buoy.id}/materials`, { filnamn, text });
            setReview(r.data);
            setReviewText(r.data.deidentifierad_text);
            setPasteText("");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Av-identifieringen misslyckades"));
        } finally {
            setBusy(null);
        }
    };

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => upload(String(reader.result), file.name);
        reader.readAsText(file);
        e.target.value = "";
    };

    const approve = async () => {
        setBusy("approve");
        try {
            await api.put(`/materials/${review.id}/approve`, { godkand_text: reviewText });
            toast.success("Av-identifierad text godkänd – analys klar");
            setReview(null);
            await loadMaterials();
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Analysen misslyckades"));
        } finally {
            setBusy(null);
        }
    };

    const reject = async () => {
        await api.put(`/materials/${review.id}/reject`);
        toast.success("Utkast avvisat – materialet raderat");
        setReview(null);
        await loadMaterials();
        refresh();
    };

    return (
        <div className="flex-1 overflow-y-auto panel-scroll p-4 space-y-4" data-testid="buoy-panel">
            <p className="text-xs text-slate-500 leading-relaxed">
                Bojar är dina insamlingspunkter. Råmaterial lagras aldrig – bara den av-identifierade, godkända analysen.
            </p>

            <div className="rounded-xl border border-border bg-abyss/40 p-3 space-y-2">
                <p className={labelCls}>Ny boj</p>
                <input data-testid="buoy-name-input" className={inputCls} value={etikett} placeholder="T.ex. Boj 2 – Handläggning"
                    onChange={(e) => setEtikett(e.target.value)} />
                <select data-testid="buoy-node-select" className={inputCls} value={nodeId} onChange={(e) => setNodeId(e.target.value)}>
                    <option value="">Koppla till nod (valfritt) …</option>
                    {nodes.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                </select>
                <button data-testid="add-buoy-button" onClick={create} disabled={!etikett.trim()}
                    className="w-full flex items-center justify-center gap-2 bg-buoy/15 border border-buoy/40 text-buoy rounded-lg py-2 text-sm font-semibold hover:bg-buoy/25 transition-colors disabled:opacity-40">
                    <MapPin className="w-4 h-4" /> Placera boj
                </button>
            </div>

            <div className="space-y-2">
                {buoys.length === 0 && <p className="text-sm text-slate-500">Inga bojar ännu.</p>}
                {buoys.map((b) => {
                    const st = BUOY_STATUS[b.status] || BUOY_STATUS.placerad;
                    return (
                        <div key={b.id} data-testid={`buoy-row-${b.id}`}
                            onClick={() => { setSelected(b.id); setReview(null); }}
                            className={`rounded-lg border p-3 cursor-pointer transition-all ${selected === b.id ? "border-buoy/60 bg-buoy/5" : "border-border bg-abyss/40 hover:border-buoy/30"}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-slate-200">{b.etikett}</div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border"
                                        style={{ color: st.color, borderColor: `${st.color}55`, background: `${st.color}14` }}>
                                        {st.label}
                                    </span>
                                    <button data-testid={`buoy-delete-${b.id}`} onClick={(e) => { e.stopPropagation(); remove(b.id); }}
                                        className="text-slate-600 hover:text-red-400 transition-colors">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                            {b.node_id && <div className="font-mono text-[9px] text-slate-500 mt-1">→ {nodes.find((n) => n.id === b.node_id)?.label || "okopplad"}</div>}
                        </div>
                    );
                })}
            </div>

            {buoy && !review && pending.length > 0 && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="buoy-pending-materials">
                    <p className={labelCls}>Väntar på din granskning ({pending.length})</p>
                    {pending.map((m) => (
                        <div key={m.id} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-slate-300 truncate">{m.filnamn}</span>
                            <button data-testid={`material-review-${m.id}`} onClick={() => { setReview(m); setReviewText(m.deidentifierad_text); }}
                                className="shrink-0 text-xs text-amber-400 border border-amber-500/40 rounded-lg px-2.5 py-1 hover:bg-amber-500/10 transition-colors">
                                Granska
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {buoy && !review && (
                <div className="rounded-xl border border-buoy/30 bg-buoy/5 p-3 space-y-3">
                    <p className={labelCls}>Ladda upp material till {buoy.etikett}</p>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                        Transkribering eller anteckningar. AI av-identifierar allt personkopplat, du granskar innan analys. Råtexten sparas aldrig.
                    </p>
                    <textarea data-testid="buoy-paste-input" className={inputCls} rows={4} value={pasteText}
                        placeholder="Klistra in transkribering eller anteckningar här …"
                        onChange={(e) => setPasteText(e.target.value)} />
                    <div className="flex gap-2">
                        <button data-testid="buoy-upload-text-button" onClick={() => upload(pasteText, "inklistrat-material.txt")} disabled={busy === "deidentify" || !pasteText.trim()}
                            className="flex-1 flex items-center justify-center gap-2 bg-buoy/15 border border-buoy/40 text-buoy rounded-lg py-2 text-xs font-semibold hover:bg-buoy/25 transition-colors disabled:opacity-40">
                            {busy === "deidentify" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                            {busy === "deidentify" ? "Av-identifierar …" : "Av-identifiera"}
                        </button>
                        <button data-testid="buoy-upload-file-button" onClick={() => fileRef.current?.click()} disabled={busy === "deidentify"}
                            className="shrink-0 border border-border text-slate-400 rounded-lg px-3 text-xs hover:border-buoy/40 hover:text-buoy transition-colors disabled:opacity-40">
                            .txt-fil
                        </button>
                        <input ref={fileRef} type="file" accept=".txt,.md,.csv" className="hidden" data-testid="buoy-file-input" onChange={onFile} />
                    </div>
                </div>
            )}

            {review && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-3" data-testid="buoy-review-box">
                    <div>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-amber-400">Granskningsruta – av-identifierad text</p>
                        <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                            Kontrollera att inget personkopplat finns kvar. Redigera vid behov. Först när du godkänner körs systemnivåanalysen.
                        </p>
                    </div>
                    <textarea data-testid="buoy-review-textarea" className={`${inputCls} font-mono text-xs`} rows={8}
                        value={reviewText} onChange={(e) => setReviewText(e.target.value)} />
                    <div className="flex gap-2">
                        <button data-testid="buoy-approve-button" onClick={approve} disabled={busy === "approve"}
                            className="flex-1 flex items-center justify-center gap-1.5 bg-buoy/15 border border-buoy/40 text-buoy rounded-lg py-2 text-xs font-semibold hover:bg-buoy/25 transition-colors disabled:opacity-50">
                            {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Godkänn & analysera
                        </button>
                        <button data-testid="buoy-reject-button" onClick={reject}
                            className="flex-1 flex items-center justify-center gap-1.5 border border-slate-600 text-slate-400 rounded-lg py-2 text-xs hover:border-red-500/50 hover:text-red-400 transition-colors">
                            <XCircle className="w-3.5 h-3.5" /> Avvisa & radera
                        </button>
                    </div>
                </div>
            )}

            {buoy?.insights && (
                <div className="rounded-xl border border-buoy/30 bg-buoy/5 p-3 space-y-2" data-testid="buoy-insights">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-buoy">Systemnivåinsikter</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{buoy.insights.sammanfattning}</p>
                    <div className="flex flex-wrap gap-1">
                        {(buoy.insights.teman || []).map((t, i) => (
                            <span key={i} className="text-[10px] bg-buoy/10 text-buoy border border-buoy/30 rounded-full px-2 py-0.5">{t}</span>
                        ))}
                    </div>
                    {(buoy.insights.flaskhalssignaler || []).map((s, i) => {
                        const info = dammInfo({ typ: s.typ });
                        return (
                            <div key={i} className="rounded-lg border p-2" style={{ borderColor: `${info.color}44`, background: `${info.color}0a` }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold" style={{ color: info.color }}>{info.label}</span>
                                    <span className="font-mono text-[9px] text-slate-500">allvarlighet {s.allvarlighet}/5</span>
                                </div>
                                <p className="text-[11px] text-slate-400 mt-0.5">{s.beskrivning}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
